import { withQuery } from "../../api/client";

/** The visits report's own address: the period and every filter live in the
 * query string, so the office can bookmark a view or send the link, and the
 * downloads are the report on the screen. */

export type ReportPeriod = "day" | "week" | "month" | "range";

export interface ReportQuery {
  period: ReportPeriod;
  /** The day the period is taken around -- empty for today. */
  on: string;
  start: string;
  end: string;
  provider_id: string;
  outcome: string;
  plan_id: string;
  region_id: string;
  q: string;
}

export const PERIODS: readonly { value: ReportPeriod; label: string }[] = [
  { value: "day", label: "Day" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
  { value: "range", label: "Range" },
];

const FIELDS = ["on", "start", "end", "provider_id", "outcome", "plan_id", "region_id", "q"] as const;

export function queryFromSearch(params: URLSearchParams, fallback: ReportPeriod = "week"): ReportQuery {
  const period = params.get("period");
  const known = PERIODS.some((p) => p.value === period);
  const query: ReportQuery = {
    period: known ? (period as ReportPeriod) : fallback,
    on: "",
    start: "",
    end: "",
    provider_id: "",
    outcome: "",
    plan_id: "",
    region_id: "",
    q: "",
  };
  for (const field of FIELDS) {
    query[field] = params.get(field) ?? "";
  }
  return query;
}

/** The query string for `query`: the screen's default period is left out, and
 * so is every empty filter, so the plain address is that period, everything. */
export function searchFromQuery(query: ReportQuery, fallback: ReportPeriod = "week"): URLSearchParams {
  const params = new URLSearchParams();
  if (query.period !== fallback) {
    params.set("period", query.period);
  }
  for (const field of FIELDS) {
    if (query[field] !== "") {
      params.set(field, query[field]);
    }
  }
  return params;
}

export function reportApiUrl(query: ReportQuery): string {
  return withQuery("/api/v1/routing/report", { period: query.period, ...Object.fromEntries(FIELDS.map((f) => [f, query[f]])) });
}

/** Clock time from minutes after midnight: 540 -> "09:00". */
export function clockTime(minutes: number): string {
  return `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
}
